import React from "react";
import { ArrowUpRight, MessageCircle, Banknote } from "lucide-react";
import { waLink } from "../lib/site";

const VIDEO_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_ashir-financeiro/artifacts/xvxbq5g4_Carta%20Contemplada%20v1.mp4";

// Navy palette: text on white background, with video on the right
export default function BuyingCTA() {
  return (
    <section
      id="compra"
      data-testid="buying-cta-section"
      className="relative bg-white py-24 md:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 10% 20%, rgba(10,31,58,0.06), transparent 45%), radial-gradient(ellipse at 90% 80%, rgba(10,31,58,0.05), transparent 55%)",
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-[#0A1F3A]/25 to-transparent" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-[#0A1F3A]/25 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text column */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-8 pl-3 pr-5 py-2 border border-[#0A1F3A]/25 bg-[#0A1F3A]/[0.03]">
              <span className="w-9 h-9 flex items-center justify-center border border-[#0A1F3A]/30">
                <Banknote size={16} strokeWidth={1.4} className="text-[#0A1F3A]" />
              </span>
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#0A1F3A] font-semibold">
                Compra imediata · Liquidez em dias
              </span>
            </div>

            <h2
              data-testid="buying-cta-headline"
              className="font-serif-ag text-[#0A1F3A] text-4xl md:text-5xl lg:text-[3.4rem] xl:text-[3.8rem] font-light leading-[1.05] tracking-[-0.02em]"
            >
              Compramos sua{" "}
              <span className="italic text-[#1E4E8C]">carta de crédito</span>{" "}
              <span className="block md:inline text-[#0A1F3A]">contemplada.</span>
            </h2>

            <p
              data-testid="buying-cta-sub"
              className="mt-7 text-lg md:text-xl font-light leading-relaxed max-w-xl"
              style={{ color: "#1E4E8C" }}
            >
              Transforme seu crédito em dinheiro com uma negociação{" "}
              <span className="text-[#0A1F3A] font-normal">segura</span> e{" "}
              <span className="text-[#0A1F3A] font-normal">transparente</span>.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waLink(
                  "Olá ASHIR GROUP, quero vender minha carta de crédito contemplada. Podem me passar as condições?"
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="buying-cta-whatsapp"
                className="group inline-flex items-center justify-center gap-3 bg-[#0A1F3A] text-white px-8 py-4 text-xs md:text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[#1E4E8C] transition-colors"
              >
                <MessageCircle size={16} strokeWidth={1.6} />
                Quero vender minha carta
                <ArrowUpRight
                  size={15}
                  strokeWidth={1.8}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
              <a
                href="#contato"
                data-testid="buying-cta-secondary"
                className="inline-flex items-center justify-center gap-3 border border-[#0A1F3A]/60 text-[#0A1F3A] px-8 py-4 text-xs md:text-sm tracking-[0.22em] uppercase font-light hover:bg-[#0A1F3A]/[0.04] transition-colors"
              >
                Solicitar avaliação
              </a>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
              {[
                { k: "Pagamento", v: "Rápido & estruturado" },
                { k: "Negociação", v: "Transparente & justa" },
                { k: "Suporte", v: "Consultor sênior dedicado" },
              ].map((s, i) => (
                <div
                  key={i}
                  data-testid={`buying-cta-pillar-${i}`}
                  className="border-t sm:border-t-0 sm:border-l border-[#0A1F3A]/15 sm:pl-5 pt-4 sm:pt-0"
                >
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#1E4E8C] font-semibold">
                    {s.k}
                  </div>
                  <div className="mt-1.5 text-[#0A1F3A] text-sm font-light">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Video column */}
          <div className="lg:col-span-5">
            <div
              data-testid="buying-cta-video-wrap"
              className="relative"
            >
              <div className="absolute -top-3 -left-3 right-6 bottom-6 border border-[#0A1F3A]/30 pointer-events-none" />
              <video
                data-testid="buying-cta-video"
                src={VIDEO_URL}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                aria-label="Vídeo institucional — Compramos sua carta contemplada"
                className="relative w-full aspect-[4/5] object-cover bg-[#0A1F3A]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
